<script setup lang="ts">
import { computed } from 'vue'

import type { ProductSpec } from '@/api/ls'
import { quotaAddonHint } from '@/lib/licenseLabels'
import { buildQuotaRows, usageFrom } from '@/lib/quotas'

/**
 * 配額 chips（批114 從 `components/LicenseDetailModal.vue` 的〈額度〉區抽出來共用）。
 *
 * 詳情類畫面用：**全部**配額鍵一個 chip，不收摘要（窄欄的摘要版是 `QuotaSummary.vue`）。
 * 包含三件原本寫在 modal 裡的事：
 * - 已達上限 → `.chip.over` 紅底＋「只擋新建」的 tooltip（`styles/ui.css` 的共用樣式）。
 * - 被加購提升的鍵 → `.has-addon` 外框＋「＋加購」小標。
 * - 「＋加購」的 tooltip 走 `lib/licenseLabels.ts::quotaAddonHint`（主證原值 → 有效值、
 *   最早到期日）——**主證原值要的是 `license.quotas`，不是傳進來的有效值**，所以那個
 *   物件由呼叫端整個帶進來。
 */
const props = defineProps<{
  /** 該產品的憑證設計；null＝還沒載回來（退化成用原始英文鍵當標籤）。 */
  spec: ProductSpec | null
  /** 要顯示的上限值——有加購時請帶**有效值**（`effective_quotas`）。 */
  quotas: Record<string, number | null> | null | undefined
  /** 心跳用量；不給／null＝只顯示上限。吃任何形狀（見 `lib/quotas.ts::usageFrom`）。 */
  usage?: unknown
  /**
   * 加購來源（`license.addon_sources.quotas`：配額鍵 → 最早到期日；鍵缺席＝這個鍵
   * 沒有被加購提升）。不給時退回 `license.addon_sources.quotas`。
   */
  addonSources?: Record<string, string | null> | null
  /**
   * 產生「＋加購」tooltip 用的授權物件：`quotas`＝**主證原值**（不是有效值）、
   * `addon_sources`＝加購來源。不給就不畫加購標記。
   */
  license?: {
    quotas?: Record<string, number | null> | null
    addon_sources?: { quotas?: Record<string, string | null> } | null
  } | null
}>()

const rows = computed(() =>
  buildQuotaRows(props.spec, props.quotas, props.usage == null ? null : usageFrom(props.usage)),
)

const addonMap = computed<Record<string, string | null>>(
  () => props.addonSources ?? props.license?.addon_sources?.quotas ?? {},
)

function hasAddon(key: string): boolean {
  return addonMap.value[key] !== undefined
}

function tipOf(key: string, limit: number | null, over: boolean): string | undefined {
  if (hasAddon(key)) {
    return quotaAddonHint(
      props.license ?? { quotas: props.quotas ?? null, addon_sources: { quotas: addonMap.value } },
      key,
      limit,
    )
  }
  return over ? '已達上限（只擋新建，既有資料照常使用）' : undefined
}
</script>

<template>
  <div class="chips">
    <span
      v-for="q in rows"
      :key="q.key"
      class="chip"
      :class="{ over: q.over, 'has-addon': hasAddon(q.key) }"
      :title="tipOf(q.key, q.limit, q.over)"
    >
      {{ q.label }} {{ q.text }}<small v-if="hasAddon(q.key)" class="ad-tag">＋加購</small>
    </span>
    <span v-if="!rows.length" class="subtle">—</span>
  </div>
</template>

<style scoped>
/* 批93-B：被加購提升的配額 chip 的小標（與 `LicensesView` 同一套） */
.chip.has-addon {
  border-color: var(--brand-600, var(--border-1));
}
.ad-tag {
  margin-left: 4px;
  color: var(--brand-600, var(--fg-3));
  font-size: var(--fs-10, 10px);
  font-weight: 600;
  cursor: help;
}
</style>
